/**
 * BuyerOfferPublicPage.jsx
 * ==========================
 * Page publique (sans auth) accessible via /buyer-offer/:token
 * Permet à l'acquéreur de formuler son offre d'achat + signer manuscritement.
 *
 * UX cible : très simple, rassurante, mobile-first, formulaire en 1 colonne,
 * signature canvas full-width.
 *
 * Statut final côté agent : "Brouillon à valider" (l'agent contrôle avant envoi).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Home, MapPin, Loader2, CheckCircle2, AlertCircle, ArrowRight,
  Euro, Eraser, Download, ShieldCheck,
} from 'lucide-react';

/* eslint-disable react/no-unescaped-entities */

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// ─── Signature Canvas component ────────────────────────────────────────────
function SignatureCanvas({ value, onChange, testid = 'signature-canvas' }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1F2937';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = getPos(e);
  };

  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    last.current = pos;
    setIsEmpty(false);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    // Export PNG
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onChange(dataUrl);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, rect.width, rect.height);
    setIsEmpty(true);
    onChange('');
  };

  return (
    <div className="space-y-2">
      <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          className="block w-full touch-none"
          style={{ height: 180, cursor: 'crosshair' }}
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={end}
          data-testid={testid}
        />
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className={isEmpty ? 'text-amber-600 font-medium' : 'text-emerald-600 font-medium'}>
          {isEmpty ? 'Signez dans le cadre ci-dessus' : '✓ Signature enregistrée'}
        </span>
        <button
          type="button"
          onClick={clear}
          className="flex items-center gap-1 text-slate-500 hover:text-slate-900 font-medium"
          data-testid="signature-clear"
        >
          <Eraser className="w-3.5 h-3.5" /> Effacer
        </button>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────
export default function BuyerOfferPublicPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invite, setInvite] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    offer_amount: '',
    financing_type: 'mixte',
    personal_contribution: '',
    loan_amount: '',
    bank_name: '',
    has_pre_approval: false,
    conditions: [],
    desired_signature_date: '',
    personal_message: '',
    signature_b64: '',
    accept_terms: false,
  });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/public/buyer-offer/${token}`)
      .then((res) => {
        if (cancelled) return;
        setInvite(res.data);
        setForm((f) => ({
          ...f,
          first_name: res.data.buyer?.first_name || '',
          last_name: res.data.buyer?.last_name || '',
          email: res.data.buyer?.email || '',
        }));
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.response?.data?.detail || "Lien invalide ou expiré");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleCondition = (cond) => {
    setForm((f) => ({
      ...f,
      conditions: f.conditions.includes(cond)
        ? f.conditions.filter((c) => c !== cond)
        : [...f.conditions, cond],
    }));
  };

  const validate = () => {
    if (!form.first_name.trim() || !form.last_name.trim()) return "Votre nom et prénom sont obligatoires";
    if (!/^.+@.+\..+$/.test(form.email)) return "Email invalide";
    const amt = Number(form.offer_amount);
    if (!amt || amt <= 0) return "Le montant de l'offre est obligatoire";
    if (!form.signature_b64) return "Veuillez signer dans le cadre prévu";
    if (!form.accept_terms) return "Vous devez accepter les conditions d'utilisation";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        offer_amount: Number(form.offer_amount),
        personal_contribution: form.personal_contribution ? Number(form.personal_contribution) : null,
        loan_amount: form.loan_amount ? Number(form.loan_amount) : null,
      };
      const res = await axios.post(`${API_BASE}/public/buyer-offer/${token}/submit`, payload);
      setSubmitted(true);
      setSubmittedData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi de l'offre");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Chargement…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6" data-testid="buyer-offer-error">
        <div className="max-w-md text-center bg-white rounded-3xl shadow-xl p-8 border border-slate-200">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Lien indisponible</h1>
          <p className="text-sm text-slate-600">{error}</p>
          <p className="text-xs text-slate-400 mt-4">Contactez votre agent immobilier pour recevoir un nouveau lien.</p>
        </div>
      </div>
    );
  }

  if (submitted && submittedData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6" data-testid="buyer-offer-success">
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="max-w-md text-center bg-white rounded-3xl shadow-xl p-8 border border-emerald-100">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-black text-slate-900 mb-2">Offre envoyée !</h1>
          <p className="text-sm text-slate-600 leading-relaxed">
            Votre offre d&apos;achat (n°<strong>{submittedData.offer_number}</strong>) a été transmise à l&apos;agent immobilier.
            Vous recevrez un récapitulatif par email.
          </p>
          <p className="text-xs text-slate-400 mt-4">
            L&apos;agent vous recontactera pour la suite (validation, signature notariale du compromis).
          </p>
          <a
            href={`${API_BASE}/public/buyer-offer/${token}/loi-pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-bold hover:bg-slate-800"
            data-testid="download-loi-btn"
          >
            <Download className="w-4 h-4" /> Télécharger ma lettre d'intention
          </a>
        </motion.div>
      </div>
    );
  }

  const prop = invite?.property || {};
  const askingPrice = invite?.asking_price;

  const conditionOptions = [
    "Obtention du prêt immobilier",
    "Vente préalable de mon bien actuel",
    "Diagnostic technique global satisfaisant",
    "Absence de servitudes lourdes",
    "Accord préalable de la banque",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1a0f2e] via-[#2d1b4d] to-[#3a2566] text-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
          <p className="text-xs uppercase tracking-[0.18em] text-white/60 font-bold mb-1">
            Offre d'achat immobilière
          </p>
          <h1 className="text-2xl sm:text-3xl font-black">Faites votre offre</h1>
          <p className="text-sm text-white/70 mt-1">
            via {invite?.agency?.name || 'votre agence partenaire'}
            {invite?.agent_name ? ` · Agent : ${invite.agent_name}` : ''}
          </p>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Property card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
              <Home className="w-5 h-5 text-orange-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Bien concerné</p>
              <p className="font-bold text-slate-900 text-base sm:text-lg leading-tight">{prop.address || '—'}</p>
              <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                <MapPin className="w-3.5 h-3.5" /> {prop.postal_code} {prop.city}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-slate-100">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Surface</p>
              <p className="text-sm font-bold text-slate-900 mt-0.5">{prop.surface_m2 ? `${prop.surface_m2} m²` : '—'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Pièces</p>
              <p className="text-sm font-bold text-slate-900 mt-0.5">{prop.rooms || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Prix demandé</p>
              <p className="text-sm font-bold text-slate-900 mt-0.5">
                {askingPrice ? `${Math.round(askingPrice).toLocaleString('fr-FR')} €` : '—'}
              </p>
            </div>
          </div>
        </div>

        {invite?.personal_message && (
          <div className="bg-orange-50 border-l-4 border-orange-400 rounded-r-xl p-4">
            <p className="text-xs text-orange-700 font-bold mb-1">Message de votre agent</p>
            <p className="text-sm text-slate-700 italic">"{invite.personal_message}"</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-5">

          {/* Identité */}
          <section>
            <h2 className="text-sm font-bold text-slate-900 mb-3">Vos coordonnées</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Prénom *">
                <input type="text" required value={form.first_name} onChange={(e) => set('first_name', e.target.value)} className={inputCls} data-testid="buyer-first-name" />
              </Field>
              <Field label="Nom *">
                <input type="text" required value={form.last_name} onChange={(e) => set('last_name', e.target.value)} className={inputCls} data-testid="buyer-last-name" />
              </Field>
              <Field label="Email *">
                <input type="email" required value={form.email} onChange={(e) => set('email', e.target.value)} className={inputCls} data-testid="buyer-email-input" />
              </Field>
              <Field label="Téléphone">
                <input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} className={inputCls} data-testid="buyer-phone" />
              </Field>
              <Field label="Adresse postale" cols={2}>
                <input type="text" value={form.address} onChange={(e) => set('address', e.target.value)} className={inputCls} data-testid="buyer-address" />
              </Field>
            </div>
          </section>

          {/* Offre */}
          <section>
            <h2 className="text-sm font-bold text-slate-900 mb-3">Votre offre</h2>
            <Field label="Prix proposé (€) *">
              <div className="relative">
                <input type="number" required min={1} step={1000} value={form.offer_amount} onChange={(e) => set('offer_amount', e.target.value)} className={`${inputCls} pr-12 text-lg font-bold`} placeholder="350000" data-testid="buyer-offer-amount" />
                <Euro className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              </div>
            </Field>
          </section>

          {/* Financement */}
          <section>
            <h2 className="text-sm font-bold text-slate-900 mb-3">Financement</h2>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[
                { v: 'cash', l: 'Comptant' },
                { v: 'credit', l: 'Crédit' },
                { v: 'mixte', l: 'Mixte' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => set('financing_type', opt.v)}
                  className={`px-3 py-2 rounded-xl border text-sm font-bold transition ${
                    form.financing_type === opt.v
                      ? 'bg-orange-500 border-orange-500 text-white shadow-md shadow-orange-500/30'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid={`buyer-fin-${opt.v}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
            {form.financing_type !== 'cash' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Apport personnel (€)">
                  <input type="number" min={0} value={form.personal_contribution} onChange={(e) => set('personal_contribution', e.target.value)} className={inputCls} data-testid="buyer-contribution" />
                </Field>
                <Field label="Montant emprunt (€)">
                  <input type="number" min={0} value={form.loan_amount} onChange={(e) => set('loan_amount', e.target.value)} className={inputCls} data-testid="buyer-loan" />
                </Field>
                <Field label="Banque (optionnel)" cols={2}>
                  <input type="text" value={form.bank_name} onChange={(e) => set('bank_name', e.target.value)} className={inputCls} placeholder="Ex: BNP Paribas, Crédit Agricole…" data-testid="buyer-bank" />
                </Field>
                <label className="flex items-center gap-2 col-span-1 sm:col-span-2">
                  <input type="checkbox" checked={form.has_pre_approval} onChange={(e) => set('has_pre_approval', e.target.checked)} className="w-4 h-4 accent-orange-500" data-testid="buyer-pre-approval" />
                  <span className="text-sm text-slate-700">J'ai déjà un accord de principe de la banque</span>
                </label>
              </div>
            )}
          </section>

          {/* Conditions */}
          <section>
            <h2 className="text-sm font-bold text-slate-900 mb-3">Conditions suspensives</h2>
            <div className="space-y-2">
              {conditionOptions.map((cond) => (
                <label key={cond} className="flex items-start gap-2 p-2.5 rounded-lg border border-slate-200 hover:border-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.conditions.includes(cond)}
                    onChange={() => toggleCondition(cond)}
                    className="w-4 h-4 mt-0.5 accent-orange-500"
                    data-testid={`buyer-cond-${cond.slice(0, 15).replace(/\s/g, '-')}`}
                  />
                  <span className="text-sm text-slate-700 leading-snug">{cond}</span>
                </label>
              ))}
            </div>
          </section>

          {/* Délai + message */}
          <section className="space-y-3">
            <Field label="Date souhaitée pour le compromis">
              <input type="date" value={form.desired_signature_date} onChange={(e) => set('desired_signature_date', e.target.value)} className={inputCls} data-testid="buyer-desired-date" />
            </Field>
            <Field label="Message à l'agent (optionnel)">
              <textarea rows={3} value={form.personal_message} onChange={(e) => set('personal_message', e.target.value)} className={inputCls} placeholder="Précisions, remarques…" data-testid="buyer-message" />
            </Field>
          </section>

          {/* Signature canvas */}
          <section>
            <h2 className="text-sm font-bold text-slate-900 mb-1">Signature manuscrite *</h2>
            <p className="text-xs text-slate-500 mb-2">Signez avec votre doigt (téléphone) ou la souris (ordinateur).</p>
            <SignatureCanvas value={form.signature_b64} onChange={(v) => set('signature_b64', v)} testid="buyer-signature-canvas" />
          </section>

          {/* Terms */}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.accept_terms}
              onChange={(e) => set('accept_terms', e.target.checked)}
              className="w-4 h-4 mt-0.5 accent-orange-500"
              data-testid="buyer-accept-terms"
            />
            <span className="text-xs text-slate-600 leading-snug">
              J'accepte que mes informations soient transmises à l'agence et à l'agent immobilier dans le cadre de ma démarche d'achat. Cette offre constitue une lettre d'intention non engageante jusqu'à la signature du compromis chez le notaire.
            </span>
          </label>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || !form.signature_b64 || !form.accept_terms}
            className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-black text-base shadow-xl shadow-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition"
            data-testid="buyer-submit-offer"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Envoi en cours…</span>
            ) : (
              <span className="flex items-center justify-center gap-2">Envoyer mon offre <ArrowRight className="w-4 h-4" /></span>
            )}
          </button>

          <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
            <ShieldCheck className="w-3 h-3" />
            Données protégées · Chiffrement de bout en bout
          </div>
        </form>

        <p className="text-center text-[11px] text-slate-400 pb-8">
          Propulsé par <strong className="text-slate-600">Homly</strong>
        </p>
      </div>
    </div>
  );
}

// ─── Tiny helpers ──────────────────────────────────────────────────────────
const inputCls = "w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 focus:outline-none transition";

function Field({ label, children, cols = 1 }) {
  return (
    <label className={`block ${cols === 2 ? 'sm:col-span-2' : ''}`}>
      <span className="block text-[11px] font-bold text-slate-600 mb-1">{label}</span>
      {children}
    </label>
  );
}
